import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Star } from 'lucide-react'
import { useCashAccounts } from '@/hooks/useCashAccounts'
import { DataTable } from '@/components/ui/data-table'
import { SearchInput } from '@/components/ui/search-input'
import { Pagination } from '@/components/ui/pagination'
import { StatusBadge } from '@/components/ui/status-badge'
import { Button } from '@/components/ui/button'
import { formatBRL } from '@/lib/currency'
import type { CashAccount } from '@/types/cash'
import type { Column } from '@/components/ui/data-table'

type ActiveFilter = 'all' | 'active' | 'inactive'

const FILTER_LABELS: Record<ActiveFilter, string> = {
  all: 'Todas',
  active: 'Ativas',
  inactive: 'Inativas',
}

const TYPE_LABELS: Record<CashAccount['type'], string> = {
  CASH: 'Dinheiro',
  BANK: 'Banco',
}

const COLUMNS: Column<CashAccount>[] = [
  {
    key: 'name',
    header: 'Conta',
    render: (row) => (
      <span className="flex items-center gap-1.5">
        {row.name}
        {row.isDefault && (
          <span title="Conta padrão" className="text-primary">
            <Star size={13} className="fill-primary" />
          </span>
        )}
      </span>
    ),
  },
  { key: 'type', header: 'Tipo', render: (row) => TYPE_LABELS[row.type] },
  {
    key: 'openingBalance',
    header: 'Saldo inicial',
    className: 'hidden lg:table-cell',
    render: (row) => formatBRL(row.openingBalance),
  },
  {
    key: 'currentBalance',
    header: 'Saldo atual',
    render: (row) => (
      <span className="font-medium tabular-nums">{formatBRL(row.currentBalance)}</span>
    ),
  },
  {
    key: 'active',
    header: 'Status',
    render: (row) => <StatusBadge active={row.active} />,
  },
]

export function CashAccountListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [page, setPage] = useState(1)

  const activeParam =
    activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined

  const { data, isLoading } = useCashAccounts({ search, active: activeParam, page })

  function handleSearch(v: string) {
    setSearch(v)
    setPage(1)
  }

  function handleFilter(f: ActiveFilter) {
    setActiveFilter(f)
    setPage(1)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Contas de caixa</h1>
          <p className="text-sm text-muted-foreground">
            Contas de dinheiro e banco da empresa
          </p>
        </div>
        <Button onClick={() => navigate('/cash/accounts/new')}>
          <Plus size={16} />
          Nova conta
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder="Buscar por nome…"
          className="flex-1"
        />
        <div className="flex overflow-hidden rounded-md border border-input text-sm">
          {(Object.keys(FILTER_LABELS) as ActiveFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => handleFilter(f)}
              className={`px-3 py-1.5 transition-colors ${
                activeFilter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted/50'
              }`}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={COLUMNS}
        data={data?.data ?? []}
        keyExtractor={(row) => row.id}
        onRowClick={(row) => navigate(`/cash/accounts/${row.id}/edit`)}
        isLoading={isLoading}
        emptyTitle="Nenhuma conta cadastrada"
        emptyDescription="Cadastre a primeira conta e informe o saldo atual."
        emptyAction={
          <Button onClick={() => navigate('/cash/accounts/new')}>
            <Plus size={16} />
            Nova conta
          </Button>
        }
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}
    </div>
  )
}
